import React from "react";
import { trpc } from "@/utils/trpc";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	Presentation,
	Users,
	Calendar,
	TrendingUp,
	PenTool,
	Plus,
	BarChart3,
	Clock,
} from "lucide-react";

export interface BoardsOverviewProps {
	workspaceId: string;
	session?: any;
	currentWorkspace?: any;
}

export const BoardsOverview: React.FC<BoardsOverviewProps> = ({
	workspaceId,
	session,
	currentWorkspace,
}) => {
	const {
		isPending,
		error,
		data: channelData,
	} = useQuery(
		trpc.channel.getByWorkspace.queryOptions({
			workspaceId: Number(workspaceId),
		}),
	);

	if (isPending) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="text-center">
					<div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-primary border-b-2" />
					<p className="text-muted-foreground">Loading boards...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="rounded-lg border border-border bg-destructive/10 p-8 text-center">
					<Presentation className="mx-auto mb-4 h-12 w-12 text-destructive" />
					<h3 className="mb-2 font-semibold text-destructive text-lg">
						Error loading boards
					</h3>
					<p className="text-destructive/80">{error.message}</p>
				</div>
			</div>
		);
	}

	// Sample board data for demonstration
	const enhancedBoards = channelData?.map((channel: any) => ({
		...channel,
		taskCount: Math.floor(Math.random() * 50) + 10,
		completedTasks: Math.floor(Math.random() * 30) + 5,
		lastActivity: new Date(
			Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
		).toISOString(),
		members: Math.floor(Math.random() * 12) + 3,
		boardType: ["Whiteboard", "Mind Map", "Diagram", "Canvas", "Sketch"][
			Math.floor(Math.random() * 5)
		],
		progress: Math.floor(Math.random() * 100),
		description: `Visual workspace for ${channel.name} collaboration`,
	}));

	return (
		<div className="flex h-full flex-col bg-background">
			{/* Header */}
			<div className="border-border border-b bg-card px-6 py-4">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="font-bold text-2xl text-foreground">Whiteboards</h1>
						<p className="mt-1 text-muted-foreground text-sm">
							Visual collaboration and brainstorming spaces
						</p>
					</div>
					<button
						type="button"
						className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
					>
						<Plus className="h-4 w-4" />
						New Whiteboard
					</button>
				</div>
			</div>

			{/* Scrollable Grid Container */}
			<div className="flex-1 overflow-y-auto p-6">
				{enhancedBoards && enhancedBoards.length > 0 ? (
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{enhancedBoards.map((board: any) => (
							<Link
								key={board.id}
								to="/$workspaceId/boards/$channelId"
								params={{ workspaceId: workspaceId, channelId: board.id }}
								className="group"
							>
								<div className="flex h-full cursor-pointer flex-col rounded-lg border border-border bg-card p-5 text-card-foreground transition-all duration-200 hover:border-accent hover:shadow-lg">
									{/* Board Header */}
									<div className="mb-3 flex items-start justify-between">
										<div className="rounded-lg bg-accent p-2 transition-colors group-hover:bg-accent/80">
											<PenTool className="h-5 w-5 text-accent-foreground" />
										</div>
										<span className="rounded bg-muted px-2 py-1 font-medium text-muted-foreground text-xs">
											{board.boardType}
										</span>
									</div>

									{/* Board Info */}
									<h3 className="mb-2 font-semibold text-card-foreground transition-colors group-hover:text-primary">
										{board.name}
									</h3>
									<p className="mb-4 line-clamp-2 flex-1 text-muted-foreground text-sm">
										{board.description}
									</p>

									{/* Progress Bar */}
									<div className="mb-3">
										<div className="mb-1 flex items-center justify-between text-muted-foreground text-xs">
											<span>Progress</span>
											<span>{board.progress}%</span>
										</div>
										<div className="h-2 w-full rounded-full bg-muted">
											<div
												className="h-2 rounded-full bg-primary transition-all duration-300"
												style={{ width: `${board.progress}%` }}
											/>
										</div>
									</div>

									{/* Board Stats */}
									<div className="grid grid-cols-2 gap-2 border-border border-t pt-3">
										<div className="flex items-center gap-1 text-muted-foreground text-xs">
											<BarChart3 className="h-3 w-3" />
											<span>{board.taskCount} tasks</span>
										</div>
										<div className="flex items-center gap-1 text-muted-foreground text-xs">
											<Users className="h-3 w-3" />
											<span>{board.members} members</span>
										</div>
										<div className="flex items-center gap-1 text-muted-foreground text-xs">
											<TrendingUp className="h-3 w-3" />
											<span>{board.completedTasks} done</span>
										</div>
										<div className="flex items-center gap-1 text-muted-foreground text-xs">
											<Clock className="h-3 w-3" />
											<span>
												{new Date(board.lastActivity).toLocaleDateString(
													undefined,
													{
														month: "short",
														day: "numeric",
													},
												)}
											</span>
										</div>
									</div>
								</div>
							</Link>
						))}
					</div>
				) : (
					<div className="flex h-full flex-col items-center justify-center">
						<div className="max-w-md rounded-lg border border-border bg-card p-8 text-center">
							<Presentation className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
							<h3 className="mb-2 font-semibold text-foreground text-lg">
								No whiteboards yet
							</h3>
							<p className="mb-4 text-muted-foreground">
								Create your first whiteboard to start visual collaboration.
							</p>
							<button
								type="button"
								className="rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
							>
								Create First Whiteboard
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};